import { EntityDescriptor, ENT_READ, PrivateRoute, Utils, EntityEditorPage, PropsFrom, FieldDescriptor, OmitFieldsOfTypeFunction, sliceEntityEditorPageOnlyForExtension, SliceEntityEditorPage, getBaseReducers, createSliceFoundation } from "@crispico/foundation-react";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { missionEntityDescriptor } from "AppEntityDescriptors";
import { MissionsAwaitingForDriversPageHOC } from "./MissionsAwaitingForDriversPage";
import { MissionEventValidationPageRRC } from "./MissionEventValidationPage";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import { DatePickerFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/DatePickerFieldEditor";
import _, { uniqueId } from "lodash";
import { GanttUtils } from "pages/gantt/GanttUtils";
import React from "react";
import { Mission2_TasksFieldDescriptor, Mission_EquipmentResourceFieldDescriptor, Mission_EquipmentTypeFieldDescriptor, Mission_HumanResourceFieldDescriptor, Mission_MissionTypeFieldDescriptor, Mission_StartEndTimeFieldDescriptor } from "./Mission2FieldDescriptors";
import { equipmentResourceEntityDescriptor } from "pages/EquipmentResource/equipmentResourceEntityDescriptor";
import { ARCHIVED, ID } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { MissionValidation, MissionValidationRRC } from "./MissionValidation";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { FilterOperators } from "@crispico/foundation-gwt-js";

const additionalDateFieldEditorProps = FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateTimeWithSecFormat });
const additionalDateFieldRendererProps = FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateTimeWithSecFormat });

const FIELDS_FROM_DATA = ["oilGroupBonNumber", "volume", "startVolumeIndex", "endVolumeIndex", "previousEndVolumeIndex",
    "hasNoFuelEvent", "vehicleAttachment", "endOfMission", "finishedByUser", "specialMissionFlightString", "hasNewDriver",
    "userName", "initialObjectsNumber", "orderOfFlightsLocked", "activatedFromFlex", "oilGroupName", "flightPriorityType",
    "flightPriorityComment", "specialMissionOverlap", "validationErrors", "oldDriverId", "additionalStatus", "vehicleCapacity"];

export class Mission2EntityDescriptor extends EntityDescriptor {

    protected customize() {
        this.isInDefaultColumnConfig(true, "creationDate", "humanResource", "equipmentResource", "startTime", "endTime", "type", "comment", "currentState", "mobileDevice", "equipmentType", "inactivityType", "startAddress", "endAddress", "creationMode", "initialStartTime", "initialEndTime")
            .removeFieldDescriptors("humanResourceId", "equipmentResourceId", "inactivityTypeId", "mobileDeviceId", "dataString", "dataVersion", "day", "currentStopId")
            .addFieldDescriptor({ name: "status", type: FieldType.dropdown })
            .addFieldDescriptor({ name: "orderOfFlightsLocked", icon: "lock" })
            .addFieldDescriptor({ name: "creationDate", type: FieldType.date, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
            .addFieldDescriptor({ name: "startTime", type: FieldType.date, updateFieldName: "initialStartTime", additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps }, new Mission_StartEndTimeFieldDescriptor())
            .addFieldDescriptor({ name: "endTime", type: FieldType.date, updateFieldName: "initialEndTime", additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps }, new Mission_StartEndTimeFieldDescriptor())
            .addFieldDescriptor({ name: "type" }, new Mission_MissionTypeFieldDescriptor())
            .addFieldDescriptor({ name: "equipmentType" }, new Mission_EquipmentTypeFieldDescriptor())
            .addFieldDescriptor({ name: "equipmentResource" }, new Mission_EquipmentResourceFieldDescriptor())
            .addFieldDescriptor({ name: "humanResource" }, new Mission_HumanResourceFieldDescriptor())
            .addFieldDescriptor({ name: "tasks", type: FieldType.oneToMany("Task"), filterable: false, sortable: false, isInDefaultColumnConfig: false, oneToManyOppositeField: "mission", isMulti: true } as OmitFieldsOfTypeFunction<FieldDescriptor>, new Mission2_TasksFieldDescriptor())
            .addFieldDescriptor({ name: "events", type: FieldType.oneToMany("MissionEvent2"), enable: false, filterable: false, sortable: false, isInDefaultColumnConfig: false, clientOnly: true, oneToManyOppositeField: "mission" } as OmitFieldsOfTypeFunction<FieldDescriptor>)
            .addFieldDescriptor({ name: "stops", type: FieldType.oneToMany("Stop2"), enable: false, filterable: false, sortable: false, isInDefaultColumnConfig: false, clientOnly: true, oneToManyOppositeField: "mission" } as OmitFieldsOfTypeFunction<FieldDescriptor>)

        this.doForFields(FIELDS_FROM_DATA, fd => fd.filterable = false);
        this.doForFields(FIELDS_FROM_DATA, fd => fd.sortable = false);

        this.infoEditor.slice = createSliceFoundation(SliceMission2EntityEditorPage).setEntityDescriptor(this);
        this.infoEditor.wrappedComponentClass = Mission2EntityEditorPage;
    }

    getGraphQlFieldsToRequest(fields?: string[]): string {
        let fieldsToRequest = super.getGraphQlFieldsToRequest(fields);
        if (fieldsToRequest.includes("tasks")) {
            // Mission2_TasksFieldDescriptor also use these 4 fields.
            fieldsToRequest += " orderOfFlightsLocked status humanResource { id } equipmentResource { id }";
        }
        return fieldsToRequest;
    }
}

export class SliceMission2EntityEditorPage extends SliceEntityEditorPage {
    initialState = {
        ...sliceEntityEditorPageOnlyForExtension.initialState,
    }

    reducers = {
        ...sliceEntityEditorPageOnlyForExtension.reducers,
        ...getBaseReducers<SliceMission2EntityEditorPage>(this)
    }

    getAdditionalGraphQl() {
        return ` equipmentResource { ${ID} ${(equipmentResourceEntityDescriptor.hasArchivedField() ? ARCHIVED : "")} ${equipmentResourceEntityDescriptor.getGraphQlFieldsToRequest(equipmentResourceEntityDescriptor.miniFields)} equipmentType { ${ID} } } `
    }
}

export class Mission2EntityEditorPage extends EntityEditorPage<PropsFrom<SliceMission2EntityEditorPage>> {

    private missionValidationRef = React.createRef<MissionValidation>();

    protected getExtraTabPanes() {
        const pannes = super.getExtraTabPanes();

        pannes.push(...[
            this.createOneToManyTabPane("MissionEvent2", "missionId", { entityField: "id", filter: Filter.createComposedForClient(FilterOperators.forComposedFilter.and, [Filter.createForClient("deleted", FilterOperators.forBoolean.equals, "false")]) }),
            {
                routeProps: { path: "/eventsValidation" }, menuItemProps: { content: _msg("MissionEventValidationPage.title") },
                render: () => <MissionEventValidationPageRRC id="missionEventValidation" entityName="Mission2" entityId={this.props.entity?.id} />
            },
            this.createOneToManyTabPane("Task", "mission", {
                sort: { field: "indexInMission", direction: "ASC" }
            })
        ])
        return pannes;
    }

    protected renderMain() {
        // used uniqueId until do the fix [#36502](https://redmine.xops-online.com/issues/36502), also this will be removed in [#34410](https://redmine.xops-online.com/issues/34410)
        return <>
            {super.renderMain()}
            <MissionValidationRRC id={uniqueId("MissionValidationMissionEditorRRC")} ref={this.missionValidationRef}
                validationPassedHandler={async () => await super.onSave()} okHandler={async () => await super.onSave()} />
        </>;
    };

    protected async onSave() {
        await Utils.setTimeoutAsync();
        this.triggerCommitForAll();
        this.missionValidationRef.current!.checkMissionValidation({
            missionEntityUid: this.props.entity.id && GanttUtils.toEntityUid("Mission2", this.props.entity.id),
            humanResourceEntityUid: this.props.entity.humanResource && GanttUtils.toEntityUid("HumanResource", this.props.entity.humanResource.id),
            equipmentResourceEntityUid: this.props.entity.equipmentResource && GanttUtils.toEntityUid("EquipmentResource", this.props.entity.equipmentResource.id),
            equipmentTypeEntityUid: this.props.entity.equipmentType && GanttUtils.toEntityUid("EquipmentType", this.props.entity.equipmentType.id),
            missionStartTime: this.props.entity.startTime && (new Date(this.props.entity.startTime).getTime() + ""),
            missionEndTime: this.props.entity.endTime && (new Date(this.props.entity.endTime).getTime() + ""),
            checkHumanResource: true
        });
    }
}

export const MissionsAwaitingForDriversPageUrl = "/MissionsAwaitingForDrivers";
export const missionsAwaitingForDriversPageRoute = () =>
    <PrivateRoute key="missionsAwaitingForDriversPage"
        path={MissionsAwaitingForDriversPageUrl}
        render={(props) => <MissionsAwaitingForDriversPageHOC {...props} id="missionsAwaitingForDriversPage" />} />

export const missionsAwaitingForDriversPageMenuEntry = () => {
    return {
        id: "missionsAwaitingForDriversPage",
        content: _msg("MissionsAwaitingForDriversPage.title"),
        to: MissionsAwaitingForDriversPageUrl, exact: true, icon: "list",
        permission: Utils.pipeJoin([ENT_READ, missionEntityDescriptor.name])
    }
};
